/**
 * 关卡总入口：章节注册表 + MatchSetup 构造器 + 数据自检
 *
 * `buildMatchSetup()` 是客户端与服务端的**唯一构造入口**：
 * 两端都用它从同一份 LevelConfig 生成 MatchSetup，复算校验才有意义。
 */

import type {
  ChapterConfig,
  Difficulty,
  LevelConfig,
  MatchSetup,
  SimConstants,
  SkillNode,
} from "../types.js";
import { getMapById } from "../maps/world.js";
import { resolveEventPool } from "../events/pool.js";
import { LIFE_SKILL_TREE } from "../skilltree/life.js";
import { CHAPTER1 } from "./chapter1.js";

export const CHAPTERS: readonly ChapterConfig[] = [CHAPTER1];

/**
 * 关卡与内容数据的版本号（GDD 8.3-6 增量分发）。
 * 数据有任何改动都要手动递增它，客户端据此判断是否需要重新拉取关卡配置。
 */
export const CONTENT_VERSION = "content-2026-10-15.1";

export function getChapterById(id: string): ChapterConfig | undefined {
  return CHAPTERS.find((c) => c.id === id);
}

export function getLevelById(id: string): LevelConfig | undefined {
  for (const chapter of CHAPTERS) {
    const found = chapter.levels.find((l) => l.id === id);
    if (found) return found;
  }
  return undefined;
}

export function getChapterLevels(chapterId: string): readonly LevelConfig[] {
  return getChapterById(chapterId)?.levels ?? [];
}

// ---------- MatchSetup 构造 ----------

export interface BuildMatchSetupOptions {
  level: LevelConfig;
  mapSeed: number;
  eventSeed: number;
  difficulty?: Difficulty;
  /** 默认使用生命火种树；后续章节可传入各自的树 */
  skillTree?: readonly SkillNode[];
  /** 元进度覆盖（MVP 不启用，保留给"局外养成"） */
  constantsOverride?: Partial<SimConstants>;
}

/**
 * 由关卡配置构造一局游戏的完整初始条件。
 * 地图只保留关卡启用的区域与路线子集，技能树只保留关卡开放的节点。
 */
export function buildMatchSetup(opts: BuildMatchSetupOptions): MatchSetup {
  const { level } = opts;
  const map = getMapById(level.mapId);
  if (!map) throw new Error(`关卡 ${level.id} 引用了未知地图: ${level.mapId}`);

  const regionSet = new Set(level.regionIds);
  const routeSet = new Set(level.routeIds);
  const nodeSet = new Set(level.skillNodeIds);
  const tree = opts.skillTree ?? LIFE_SKILL_TREE;

  return {
    map: {
      ...map,
      regions: map.regions.filter((r) => regionSet.has(r.id)),
      routes: map.routes.filter((r) => routeSet.has(r.id)),
    },
    skillTree: tree.filter((n) => nodeSet.has(n.id)),
    difficulty: opts.difficulty ?? "normal",
    mapSeed: opts.mapSeed,
    eventSeed: opts.eventSeed,
    mistGrowth: level.mistGrowth,
    goalEnlightenment: level.goalEnlightenment,
    startRegionId: level.startRegionId,
    // turnLimit 以关卡顶层字段为准（关卡级常量覆盖仍可显式改写它）
    constants: {
      turnLimit: level.turnLimit,
      ...level.constants,
      ...opts.constantsOverride,
    },
    eventPool: resolveEventPool(level.eventPoolIds),
    eventChance: level.eventChance,
  };
}

// ---------- 数据自检 ----------

/**
 * 关卡配置自检：把"运行时才炸"的引用错误提前成启动即抛。
 * 返回可读的问题清单（空数组 = 通过），不抛错，方便测试与 CLI 报告。
 */
export function checkLevel(level: LevelConfig): string[] {
  const problems: string[] = [];
  const map = getMapById(level.mapId);
  if (!map) {
    problems.push(`未知地图: ${level.mapId}`);
    return problems;
  }

  const mapRegions = new Map(map.regions.map((r) => [r.id, r]));
  const mapRoutes = new Map(map.routes.map((r) => [r.id, r]));

  // 1. 区域引用
  const regionSet = new Set(level.regionIds);
  for (const id of level.regionIds) {
    if (!mapRegions.has(id)) problems.push(`未在 ${map.id} 中定义的区域: ${id}`);
  }

  // 2. 路线引用 + 两端都必须在本关区域集合内
  for (const id of level.routeIds) {
    const route = mapRoutes.get(id);
    if (!route) {
      problems.push(`未在 ${map.id} 中定义的路线: ${id}`);
      continue;
    }
    if (!regionSet.has(route.from)) problems.push(`路线 ${id} 的起点 ${route.from} 不在本关区域集合内`);
    if (!regionSet.has(route.to)) problems.push(`路线 ${id} 的终点 ${route.to} 不在本关区域集合内`);
  }

  // 3. 起始区域
  if (!regionSet.has(level.startRegionId)) {
    problems.push(`起始区域 ${level.startRegionId} 不在本关区域集合内`);
  }

  // 4. 技能树：节点存在、前置闭包完整、绑定卡片在本关卡池内
  const nodeIds = new Set(level.skillNodeIds);
  const cardSet = new Set(level.cardIds);
  const treeById = new Map<string, SkillNode>(LIFE_SKILL_TREE.map((n) => [n.id, n]));
  for (const id of level.skillNodeIds) {
    const node = treeById.get(id);
    if (!node) {
      problems.push(`未知的技能节点: ${id}`);
      continue;
    }
    if (node.parentId && !nodeIds.has(node.parentId)) {
      problems.push(`技能节点 ${id} 缺少前置节点 ${node.parentId}`);
    }
    if (node.cardId && !cardSet.has(node.cardId)) {
      problems.push(`技能节点 ${id} 绑定的知识卡 ${node.cardId} 不在本关卡池内`);
    }
  }

  // 5. 事件池可解析
  try {
    resolveEventPool(level.eventPoolIds);
  } catch (err) {
    problems.push(err instanceof Error ? err.message : String(err));
  }

  // 6. 连通性：所有启用区域必须能从起始区域经启用路线抵达（否则关卡不可解）
  if (regionSet.has(level.startRegionId)) {
    const adjacency = new Map<string, string[]>();
    const link = (a: string, b: string): void => {
      const list = adjacency.get(a);
      if (list) list.push(b);
      else adjacency.set(a, [b]);
    };
    for (const id of level.routeIds) {
      const route = mapRoutes.get(id);
      if (!route) continue;
      link(route.from, route.to);
      link(route.to, route.from);
    }
    const seen = new Set<string>([level.startRegionId]);
    const queue = [level.startRegionId];
    while (queue.length > 0) {
      const current = queue.shift()!;
      for (const next of adjacency.get(current) ?? []) {
        if (!seen.has(next)) {
          seen.add(next);
          queue.push(next);
        }
      }
    }
    for (const id of level.regionIds) {
      if (!seen.has(id)) problems.push(`区域 ${id} 无法从起始区域抵达（关卡不可解）`);
    }
  }

  // 7. 数值合理性
  if (!(level.goalEnlightenment > 0 && level.goalEnlightenment <= 100)) {
    problems.push(`目标启蒙度必须在 (0, 100]: ${level.goalEnlightenment}`);
  }
  if (level.turnLimit <= 0) problems.push(`回合上限必须为正: ${level.turnLimit}`);
  if (level.constants?.turnLimit !== undefined && level.constants.turnLimit !== level.turnLimit) {
    problems.push(
      `constants.turnLimit (${level.constants.turnLimit}) 与顶层 turnLimit (${level.turnLimit}) 冲突`,
    );
  }
  if (level.mistGrowth < 0) problems.push(`迷雾增长不能为负: ${level.mistGrowth}`);
  if (!(level.eventChance >= 0 && level.eventChance <= 1)) {
    problems.push(`事件概率必须在 [0, 1]: ${level.eventChance}`);
  }
  if (level.eventChance > 0 && level.eventPoolIds.length === 0) {
    problems.push("事件概率大于 0 但事件池为空");
  }

  return problems;
}

/** 校验全部章节的关卡数据，有问题直接抛错（供测试与 CI 使用） */
export function assertAllLevelsValid(): void {
  const problems: string[] = [];
  for (const chapter of CHAPTERS) {
    for (const level of chapter.levels) {
      problems.push(...checkLevel(level).map((p) => `[${level.id}] ${p}`));
    }
  }
  if (problems.length > 0) {
    throw new Error(`关卡数据校验失败:\n${problems.join("\n")}`);
  }
}
